import { Button, Checkbox, Grid, Image, Select, SelectOption, Text, TextField } from '@/atoms';
import { useAccountants } from '@/contexts/accountants/AccountantsProvider';
import { Accountant } from '@/contexts/accountants/types';
import { useDebounce } from '@/hooks/useDebounce';
import { fetchApi } from '@/utils/fetchApi';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { t } from 'ttag';
import styles from './Filter.module.scss';
import { GeoLocationSearch } from './components/GeolocationSearch/GeolocationSearch';
import { Choice, MultiChoiceFilter, filterMatch } from './components/MultiChoiceFilter/MultiChoiceFilter';

export const filterChoices = [
	{
		key: 'bokföring / redovisning',
		whitelist: ['bokföring', 'bokföra', 'redovisning', 'redovisa'],
		blacklist: ['årsredovisning', 'årsbokslut'],
	},
	{ key: 'deklaration', whitelist: ['deklaration', 'deklarera'], blacklist: [] },
	{ key: 'fakturering', whitelist: ['fakturering', 'faktura', 'fakturera'], blacklist: [] },
	{
		key: 'koncern',
		whitelist: ['koncernredovisning', 'koncerner', 'koncernbolag', 'koncernstruktur'],
		blacklist: [],
	},
	{ key: 'lön', whitelist: ['lön'], blacklist: ['lönsam'] },
	{ key: 'moms', whitelist: ['moms'], blacklist: [] },
	{ key: 'skatt', whitelist: ['skatt'], blacklist: [] },
	{ key: 'årsredovisning / bokslut', whitelist: ['bokslut', 'årsredovisning'], blacklist: [] },
];

const distanceOptions: SelectOption[] =
	['10', '30', '50']?.map((distance) => ({
		label: t`${distance} km`,
		id: distance,
		value: distance,
	})) ?? [];

interface FilterProps {
	maxPerPage?: number;
}

export const Filter = ({ maxPerPage = 12 }: FilterProps) => {
	const {
		searchAddress,
		setSearchAddress,
		accountants,
		setAccountants,
		setTotalPages,
		setFilteredAccountants,
		geoLocation,
		geoLocationError,
		setCurrentPage,
		setActiveFilters,
		searchInput,
		setSearchInput,
	} = useAccountants();

	const [selectedFilterChoices, setSelectedFilterChoices] = useState<Choice[]>([]);
	const [selectedDistance, setSelectedDistance] = useState('50');
	const [showAll, setShowAll] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	const router = useRouter();
	const pathname = usePathname();

	const selectedDistanceOption = distanceOptions.filter((option) => option.value === selectedDistance)?.[0] || null;

	useDebounce(
		() => {
			fetchAccountants();
		},
		[searchAddress, geoLocation],
		1000,
	);

	const fetchAccountants = async () => {
		try {
			let result: Accountant[] = [];
			setIsLoading(true);
			if (geoLocation) {
				setShowAll(false);
				result = await fetchApi('accountants-v1', undefined, geoLocation.latitude, geoLocation.longitude).then(
					(res) => res.body,
				);
			} else {
				searchAddress ? setShowAll(false) : setShowAll(true);
				updateSearchParams(searchAddress);
				result = await fetchApi('accountants-v1', searchAddress).then((res) => res.body);
			}
			setFilteredAccountants([]);
			setAccountants(result);
			setTotalPages(Math.ceil(result.length / maxPerPage));
			setIsLoading(false);
		} catch {
			setFilteredAccountants([]);
			setAccountants([]);
			setTotalPages(0);
			setIsLoading(false);
		}
	};

	const updateSearchParams = (address?: string) => {
		const params = new URLSearchParams();

		if (searchAddress && address) {
			params.set('address', address);
		}

		const queryString = params.toString();
		const updatedPath = queryString ? `${pathname}?${queryString}` : pathname;

		router.push(updatedPath, { scroll: false });
	};

	useEffect(() => {
		setActiveFilters(selectedFilterChoices.length > 0);

		let tempAccountants = [...accountants];

		tempAccountants = showAll
			? tempAccountants
			: tempAccountants.filter((a) => a.distance === undefined || a.distance < Number(selectedDistance) * 1000);

		const filteredResult = tempAccountants.filter(
			(a) => a.description && filterMatch(selectedFilterChoices, a.description),
		);

		setFilteredAccountants(filteredResult);
		setCurrentPage(1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFilterChoices, selectedDistance]);

	return (
		<Grid className={styles.searchFieldContainer} columns={{ base: 12 }} colGap="075" rowGap="075">
			<Grid.Item colStart={{ base: 1 }} colEnd={{ base: 13, xl: 7 }} className={styles.gridItem}>
				<div className={styles.searchField}>
					<TextField
						id="find-accountants-search"
						bordered
						maxLength={40}
						type="text"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								setSearchAddress(searchInput ?? '');
							}
						}}
						title={geoLocation ? t`Din position` : t`Sök plats`}
						disabled={geoLocation !== null}
					/>
					{!geoLocation && geoLocationError && (
						<div className={styles.geolocationError}>{t`Platsåtkomst har nekats`}</div>
					)}
				</div>
				<div className={styles.iconContainer}>
					<GeoLocationSearch />
				</div>
				<div className={styles.searchButton}>
					<Button
						className={styles.button}
						onClick={() => setSearchAddress(searchInput ?? '')}
						size="medium"
						isLoading={isLoading}
						disabled={geoLocation !== null}
					>{t`Sök`}</Button>
				</div>
			</Grid.Item>

			<Grid.Item
				colStart={{ base: 1, md: 6, xl: 7 }}
				colEnd={{ base: 13 }}
				rowStart={{ base: 3, md: 2, xl: 1 }}
				rowSpan={{ base: 2 }}
			>
				<div className={styles.showAllContainer}>
					<div>
						<Checkbox
							bordered={true}
							checked={showAll}
							className={styles.checkbox}
							onClick={() => {
								setShowAll((prevState) => !prevState);
								setSearchAddress('');
								setSearchInput('');
							}}
							label={t`Visa byråer i hela Sverige`}
						/>
						<Text marginBottom="none" className={styles.showAllText}>
							{t`Det mesta kan idag göras digitalt, så att jobba med en byrå på andra sidan Sverige är enkelt!`}
						</Text>
					</div>
					<div className={styles.mapIcon}>
						<Image src={'/icons/map.svg'} width={28} height={65} alt={t`Redovisningsbyråer i hela Sverige`} />
					</div>
				</div>
			</Grid.Item>
			<Grid.Item colStart={{ base: 1 }} colEnd={{ base: 3 }} rowStart={{ base: 2 }}>
				<Select
					hideXMark={true}
					className={styles.selectDistance}
					data={distanceOptions}
					disabled={showAll}
					onSelect={(value) => {
						setSelectedDistance(value);
					}}
					title={t`Sökradie`}
					aria-label={t`Sökradie`}
					selected={selectedDistanceOption}
					id="find-accountants-distance"
				/>
			</Grid.Item>
			<Grid.Item colSpan={{ base: 12 }} className={styles.multiChoiceContainer}>
				<MultiChoiceFilter
					title={t`Vad behöver du hjälp med?`}
					text={t`Visa endast byråer med följande erbjudande i sin beskrivning`}
					selectedChoices={selectedFilterChoices}
					setSelectedChoices={setSelectedFilterChoices}
					choices={filterChoices}
				/>
			</Grid.Item>
		</Grid>
	);
};
